// src/pages/tariff/TariffDetailsPage.js - Organized in three rows
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Breadcrumbs,
  Link,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import BusinessIcon from '@mui/icons-material/Business';
import { getTariff, deleteTariff } from '../../api/tariffAPI';
import { getCompany } from '../../api/companyAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const TariffDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const tariffId = parseInt(id);

  // Fetch tariff details
  const { data: tariff, isLoading, isError, error } = useQuery({
    queryKey: ['tariff', tariffId],
    queryFn: () => getTariff(tariffId),
    enabled: !!tariffId && !isNaN(tariffId)
  });

  // Fetch company details once we have the tariff
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', tariff?.TariffsCompanyId],
    queryFn: () => getCompany(tariff.TariffsCompanyId),
    enabled: !!tariff?.TariffsCompanyId
  });

  // Delete tariff mutation
  const deleteMutation = useMutation({
    mutationFn: deleteTariff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tariffs'] });
      if (tariff?.TariffsCompanyId) {
        queryClient.invalidateQueries({ queryKey: ['company-tariffs', tariff.TariffsCompanyId] });
      }
      
      setSnackbar({
        open: true,
        message: 'Tariff deleted successfully',
        severity: 'success'
      });
      
      // Navigate to tariffs list after deletion
      setTimeout(() => {
        navigate('/tariffs');
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting tariff: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(tariffId);
    } catch (err) {
      // Error is handled by the mutation
    }
    setDeleteDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatCurrency = (amount) => {
    return amount ? `$${parseFloat(amount).toFixed(2)}` : '-';
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    // Convert HH:MM:SS to readable format
    return timeStr.substring(0, 5); // Get HH:MM
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !tariff) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading tariff'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4">
            <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {tariff.TariffsName}
          </Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/tariffs" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Tariffs
        </Button>
      </Box>

      {/* Tariff Details Card - Organized in Three Rows */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Tariff Information</Typography>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              component={RouterLink}
              to={`/tariffs/${tariff.TariffsId}/edit`}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* First Row - Basic Info */}
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tariff Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {tariff.TariffsName}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Company
              </Typography>
              <Typography variant="body1">
                {company ? company.CompanyName : `Company ID: ${tariff.TariffsCompanyId}`}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={tariff.TariffsEnabled ? "Enabled" : "Disabled"}
                color={tariff.TariffsEnabled ? "success" : "default"}
                size="small"
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Type
              </Typography>
              <Typography variant="body1">
                {tariff.TariffsType || '-'}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Charged Per
              </Typography>
              <Typography variant="body1">
                {tariff.TariffsPer || '-'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        {/* Second Row - Pricing Info */}
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fixed Start Fee
              </Typography>
              <Typography variant="body1">
                {formatCurrency(tariff.TariffsFixedStartFee)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Daytime Rate
              </Typography>
              <Typography variant="body1">
                {formatCurrency(tariff.TariffsRateDaytime)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Nighttime Rate
              </Typography>
              <Typography variant="body1">
                {formatCurrency(tariff.TariffsRateNighttime)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Idle Charging Fee
              </Typography>
              <Typography variant="body1">
                {formatCurrency(tariff.TariffsIdleChargingFee)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Idle Apply After
              </Typography>
              <Typography variant="body1">
                {tariff.TariffsIdleApplyAfter ? `${tariff.TariffsIdleApplyAfter} minutes` : '-'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        {/* Third Row - Time Info and Other Details */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Daytime Hours
              </Typography>
              <Typography variant="body1">
                {`${formatTime(tariff.TariffsDaytimeFrom)} - ${formatTime(tariff.TariffsDaytimeTo)}`}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Nighttime Hours
              </Typography>
              <Typography variant="body1">
                {`${formatTime(tariff.TariffsNighttimeFrom)} - ${formatTime(tariff.TariffsNighttimeTo)}`}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created On
              </Typography>
              <Typography variant="body1">
                {new Date(tariff.TariffsCreated).toLocaleString('en-GB')}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last Updated
              </Typography>
              <Typography variant="body1">
                {new Date(tariff.TariffsUpdated).toLocaleString('en-GB')}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tariff ID
              </Typography>
              <Typography variant="body1">
                {tariff.TariffsId}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Tariff</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{tariff.TariffsName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TariffDetailsPage;